import { ArrowRight, Shield } from 'lucide-react';
import { FOUNDING_MEMBER_PRICE, FOUNDING_MEMBER_SPOTS } from '../constants';

interface HeroSectionProps {
  onOpenSignup: () => void;
  spotsTaken: number;
}

export default function HeroSection({ onOpenSignup, spotsTaken }: HeroSectionProps) {
  const spotsLeft = FOUNDING_MEMBER_SPOTS - spotsTaken;

  return (
    <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center justify-center px-4 pt-14 pb-2 md:pb-4 overflow-hidden">
      {/* Gym background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#161b26] via-[#161b26]/95 to-[#161b26]" />
      <div className="absolute inset-0 opacity-[0.10]" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1570829460005-c840387bb1ca?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }} />

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        {/* Founding member badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 text-xs font-medium">
          <Shield className="w-3.5 h-3.5" />
          <span>{spotsTaken} of {FOUNDING_MEMBER_SPOTS} founding spots taken — ${FOUNDING_MEMBER_PRICE}/mo, locked for life</span>
        </div>

        {/* Clean headline */}
        <h1 className="text-2xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 md:mb-5 tracking-tight">
          Stop depending on manufacturers.
          <br />
          <span className="text-blue-400">Run your own equipment.</span>
        </h1>

        {/* Supporting copy */}
        <p className="text-sm md:text-lg text-white/60 mb-6 md:mb-8 max-w-xl mx-auto leading-relaxed">
          AthnxGym gives private gym owners the knowledge, tools, and support
          to diagnose, maintain, and repair their equipment — without $400+ service calls
          or 40-minute hold times.
        </p>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={onOpenSignup}
            className="group flex items-center gap-2 px-6 py-3 md:px-7 md:py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-bold text-sm md:text-base rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
          >
            Become a Founding Member — ${FOUNDING_MEMBER_PRICE}/mo
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href="#pricing"
            className="text-white/40 hover:text-white transition-colors text-sm font-medium underline underline-offset-4"
          >
            See all plans
          </a>
        </div>

        <p className="mt-6 text-white/30 text-xs">
          Only {spotsLeft} founding member spots remaining. ${FOUNDING_MEMBER_PRICE}/month locked forever.
        </p>
      </div>
    </section>
  );
}
